import { skipToken } from '@reduxjs/toolkit/query'
import { Modal, notification } from 'antd'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { useQueryParam } from 'use-query-params'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useUnlockKolSnapshotMutation } from '@/api/workspace-api/kol-api'
import { useFetchQuotaWalletUsageQuery } from '@/api/workspaces-api'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'
import logger from '@/utils/logger'
import { isFetchBaseQueryError } from '@/utils/redux-toolkit'

export enum StatusCode {
  QuotaExhausted = 402,
  Unlocked = 409,
}

type UseUnlockKolSnapshot = (
  showVipOnlyModal: VoidFunction,
  from?: string,
  isClickedFromDrawer?: boolean,
) => {
  handleUnlock: (
    kolInfo: Pick<
      KolSchemaModel,
      'id' | 'customizedTagsNames' | 'countryCode' | 'name' | 'isExpired'
    >,
  ) => Promise<boolean>
  isUnlocking: boolean
}

const useUnlockKolSnapshot: UseUnlockKolSnapshot = (
  showVipOnlyModal,
  injectFrom,
  isClickedFromDrawer,
) => {
  const router = useRouter()
  const { formatMessage } = useIntl()
  const [queryFrom = 'KOL Detail'] = useQueryParam<string>('from')

  const from = injectFrom ?? queryFrom

  const { data: userStatus } = useFetchUserStatusQuery()
  const { data: quotaWalletResponse } = useFetchQuotaWalletUsageQuery(
    userStatus?.currentWorkspaceId
      ? { workspaceId: userStatus.currentWorkspaceId }
      : skipToken,
  )
  const [unlockKolSnapshot, { isLoading: unlockKolSnapshotIsLoading }] =
    useUnlockKolSnapshotMutation()

  const handleUnlock: ReturnType<UseUnlockKolSnapshot>['handleUnlock'] =
    useCallback(
      async (kolInfo) => {
        if (quotaWalletResponse?.quota?.remainingQuota === 0) {
          showVipOnlyModal()
          ampli.popUpPurchaseQuotaNotify()
          return false
        }

        ampli.clickUnlockKolData({
          kolCategory: kolInfo.customizedTagsNames,
          kolCountry: kolInfo.countryCode ?? undefined,
          kolId: kolInfo.id,
          kolName: kolInfo.name,
          from,
          isClickedFromDrawer,
        })

        try {
          await unlockKolSnapshot({
            kolUuid: kolInfo.id,
            from,
            is_clicked_from_drawer: isClickedFromDrawer,
          }).unwrap()

          notification.success({
            message: formatMessage({ id: 'kol:description_unlock_success' }),
            placement: 'bottomRight',
          })

          return true
        } catch (e) {
          if (!isFetchBaseQueryError(e)) {
            logger.error(e)

            notification.error({
              message: formatMessage({ id: 'general:update_failure' }),
              placement: 'bottomRight',
            })

            return false
          }

          switch (e.status) {
            case StatusCode.QuotaExhausted:
              showVipOnlyModal()
              return false
            case StatusCode.Unlocked:
              Modal.warning({
                maskClosable: true,
                title: formatMessage({
                  id: 'kol:simultaneous_unlock_by_collaborators',
                }),
                content: formatMessage({
                  id: 'kol:data_already_unlocked_by_collaborators',
                }),
                okText: formatMessage({ id: 'general:refresh_btn' }),
                onOk: (): void => {
                  router.reload()
                },
              })
              return true
          }

          return false
        }
      },
      [
        formatMessage,
        from,
        isClickedFromDrawer,
        quotaWalletResponse?.quota?.remainingQuota,
        router,
        showVipOnlyModal,
        unlockKolSnapshot,
      ],
    )

  return {
    handleUnlock,
    isUnlocking: unlockKolSnapshotIsLoading,
  }
}

export default useUnlockKolSnapshot
